import { html, nothing } from "lit";
import {
  buildChatTargetOptions,
  resolveSelectedTargetValue,
  type ChatTargetOption,
} from "../chat/chat-composer-targets.ts";
import {
  resolveContextUsage,
  resolveDefaultModelRef,
  resolveSessionModelRef,
} from "../chat/chat-context-usage.ts";
import type { GatewayModelChoice } from "../chat/delegate-router.ts";
import {
  FLEET_COMPOSER_MODES,
  labelForFleetComposerMode,
  titleForFleetComposerMode,
  type FleetComposerMode,
} from "../chat/fleet-composer-mode.ts";
import { resolveMainSessionKey } from "../chat/resolve-main-session.ts";
import { groupModelsByProvider, modelRefFromChoice } from "../controllers/chat-models.ts";
import type { GatewayHelloOk } from "../gateway.ts";
import type { AgentsListResult, SessionsListResult } from "../types.ts";

export type ChatComposerToolbarProps = {
  sessionKey: string;
  connected: boolean;
  hello: GatewayHelloOk | null;
  sessions: SessionsListResult | null;
  employees: Array<{ agentId: string; employee: { name: string; role?: string } }>;
  agents: AgentsListResult["agents"];
  modelCatalog: GatewayModelChoice[];
  modelCatalogLoading: boolean;
  modelCatalogError: string | null;
  modelPatching: boolean;
  chatSending?: boolean;
  chatMessages?: unknown[];
  composerMode: FleetComposerMode;
  onComposerModeChange: (mode: FleetComposerMode) => void;
  onSelectTarget: (sessionKey: string) => void;
  onSelectModel: (modelRef: string) => void;
};

export function renderChatComposerToolbar(props: ChatComposerToolbarProps) {
  const sessionRow = props.sessions?.sessions?.find((row) => row.key === props.sessionKey);
  const defaults = props.sessions?.defaults;
  const usage = resolveContextUsage(sessionRow, defaults, {
    messages: props.chatMessages,
    pending: props.chatSending,
  });
  const mainSessionKey = resolveMainSessionKey(props.hello, props.sessions);
  const targets = buildChatTargetOptions({
    mainSessionKey,
    sessionKey: props.sessionKey,
    employees: props.employees,
    agents: props.agents,
    sessions: props.sessions?.sessions ?? [],
  });
  const targetValue = resolveSelectedTargetValue(props.sessionKey, targets);
  const currentModelRef = resolveSessionModelRef(sessionRow, defaults);
  const defaultModelRef = resolveDefaultModelRef(defaults);
  const modelGroups = groupModelsByProvider(props.modelCatalog);
  const modelBusy = props.modelCatalogLoading || props.modelPatching;

  return html`
    <div class="chat-composer-toolbar" aria-label="Chat session controls">
      <label class="chat-composer-toolbar__field chat-composer-toolbar__field--mode">
        <span class="chat-composer-toolbar__label">Mode</span>
        <select
          class="chat-composer-toolbar__select chat-composer-toolbar__mode"
          .value=${props.composerMode}
          ?disabled=${!props.connected}
          title=${titleForFleetComposerMode(props.composerMode)}
          aria-label="Composer mode"
          @change=${(e: Event) => {
            const next = (e.target as HTMLSelectElement).value as FleetComposerMode;
            if (next && next !== props.composerMode) {
              props.onComposerModeChange(next);
            }
          }}
        >
          ${FLEET_COMPOSER_MODES.map(
            (mode) => html`
              <option value=${mode} ?selected=${mode === props.composerMode}>
                ${labelForFleetComposerMode(mode)}
              </option>
            `,
          )}
        </select>
      </label>

      <label class="chat-composer-toolbar__field">
        <span class="chat-composer-toolbar__label">Target</span>
        <select
          class="chat-composer-toolbar__select"
          .value=${targetValue}
          ?disabled=${!props.connected}
          title="Switch which agent or main session this tab talks to."
          aria-label="Chat target"
          @change=${(e: Event) => {
            const next = (e.target as HTMLSelectElement).value;
            if (next && next !== targetValue) {
              props.onSelectTarget(next);
            }
          }}
        >
          ${renderTargetOptions(targets, targetValue)}
        </select>
      </label>

      <label class="chat-composer-toolbar__field chat-composer-toolbar__field--model">
        <span class="chat-composer-toolbar__label">Model</span>
        <select
          class="chat-composer-toolbar__select"
          .value=${currentModelRef}
          ?disabled=${!props.connected || modelBusy}
          title="Model for this session (saved via sessions.patch)."
          aria-label="Session model"
          @change=${(e: Event) => {
            const next = (e.target as HTMLSelectElement).value;
            if (next !== currentModelRef) {
              props.onSelectModel(next);
            }
          }}
        >
          <option value="" ?selected=${!currentModelRef}>
            ${props.modelCatalogLoading ? "Loading models…" : defaultModelRef ? `Default (${defaultModelRef})` : "Default"}
          </option>
          ${modelGroups.map(
            (group) => html`
              <optgroup label=${group.label}>
                ${group.models.map((model) => {
                  const ref = modelRefFromChoice(model);
                  return html`
                    <option value=${ref} ?selected=${ref === currentModelRef}>${model.name}</option>
                  `;
                })}
              </optgroup>
            `,
          )}
          ${
            currentModelRef &&
            !props.modelCatalog.some((m) => modelRefFromChoice(m) === currentModelRef)
              ? html`<option value=${currentModelRef} selected>${currentModelRef}</option>`
              : nothing
          }
        </select>
      </label>

      <div
        class="chat-composer-toolbar__context ${usage.percent != null && usage.percent >= 85 ? "is-high" : ""}"
        title=${
          props.chatSending
            ? "Context fill updates after the agent finishes this reply (not while you type)."
            : usage.limit
              ? `Last reply context: ${usage.used.toLocaleString()} / ${usage.limit.toLocaleString()} tokens (${usage.percent ?? 0}%)${usage.modelLabel ? ` · ${usage.modelLabel}` : ""}`
              : usage.used > 0
                ? `Tokens from last reply: ${usage.used.toLocaleString()}${usage.modelLabel ? ` · ${usage.modelLabel}` : ""}`
                : "Context fill appears after the first completed agent reply."
        }
        aria-label="Context usage"
      >
        ${renderContextRing(usage.percent, props.chatSending)}
        <span class="chat-composer-toolbar__context-text">
          <span class="chat-composer-toolbar__label">Context</span>
          <span class="chat-composer-toolbar__context-value mono">
            ${usage.percent != null ? `${usage.percent}%` : usage.label}
          </span>
          ${
            usage.percent != null
              ? html`<span class="chat-composer-toolbar__context-detail mono">${usage.label}</span>`
              : nothing
          }
        </span>
      </div>

      ${
        props.modelCatalogError
          ? html`<span class="chat-composer-toolbar__error muted">${props.modelCatalogError}</span>`
          : nothing
      }
      ${renderDestinationChip(targets, targetValue, currentModelRef, props.sessionKey)}
    </div>
  `;
}

/**
 * UX-PLAN Pillar F — destination chip. Shows the operator where the
 * next message will land: agent/main label, model ref, and a short
 * session key. Reuses `.chip` so it inherits the existing chip
 * styling; the chip is read-only (the picker selects the actual
 * values).
 */
function renderDestinationChip(
  targets: ReturnType<typeof buildChatTargetOptions>,
  selectedTarget: string,
  modelRef: string,
  sessionKey: string,
) {
  const target = targets.find((opt) => opt.sessionKey === selectedTarget);
  const label = target?.label ?? selectedTarget;
  const modelShort = modelRef ? (modelRef.split("/").pop() ?? modelRef) : "default";
  const sessionShort = sessionKey.length > 24 ? `${sessionKey.slice(0, 21)}…` : sessionKey;
  return html`
    <span
      class="chip chat-composer-toolbar__destination"
      data-testid="chat-composer-destination"
      title="The next message will land here"
      aria-label=${`Destination: ${label}, model ${modelShort}, session ${sessionShort}`}
    >
      <span class="chat-composer-toolbar__destination-arrow" aria-hidden="true">→</span>
      <span class="chat-composer-toolbar__destination-target">${label}</span>
      <span class="chat-composer-toolbar__destination-sep" aria-hidden="true">·</span>
      <span class="chat-composer-toolbar__destination-model mono">${modelShort}</span>
      <span class="chat-composer-toolbar__destination-sep" aria-hidden="true">·</span>
      <span class="chat-composer-toolbar__destination-session mono">${sessionShort}</span>
    </span>
  `;
}

function renderContextRing(percent: number | null, pending?: boolean) {
  if (percent == null) {
    return html`
      <span
        class="chat-composer-toolbar__ring chat-composer-toolbar__ring--empty ${pending ? "chat-composer-toolbar__ring--pending" : ""}"
        aria-hidden="true"
      >
        <span class="chat-composer-toolbar__ring-track"></span>
      </span>
    `;
  }
  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return html`
    <span
      class="chat-composer-toolbar__ring ${pending ? "chat-composer-toolbar__ring--pending" : ""}"
      role="meter"
      aria-valuenow=${percent}
      aria-valuemin="0"
      aria-valuemax="100"
    >
      <svg viewBox="0 0 36 36" width="32" height="32" aria-hidden="true">
        <circle
          class="chat-composer-toolbar__ring-track"
          cx="18"
          cy="18"
          r=${radius}
          fill="none"
          stroke-width="3"
        ></circle>
        <circle
          class="chat-composer-toolbar__ring-fill"
          cx="18"
          cy="18"
          r=${radius}
          fill="none"
          stroke-width="3"
          stroke-dasharray=${`${circumference} ${circumference}`}
          stroke-dashoffset=${offset}
          transform="rotate(-90 18 18)"
        ></circle>
      </svg>
    </span>
  `;
}

function renderTargetOptions(targets: ChatTargetOption[], selectedKey: string) {
  const groups: Array<{ id: ChatTargetOption["group"]; label: string }> = [
    { id: "main", label: "Main" },
    { id: "employee", label: "Employees" },
    { id: "agent", label: "Agents" },
  ];
  return groups.map((group) => {
    const entries = targets.filter((t) => t.group === group.id);
    if (entries.length === 0) {
      return nothing;
    }
    return html`
      <optgroup label=${group.label}>
        ${entries.map(
          (entry) => html`
            <option value=${entry.sessionKey} ?selected=${entry.sessionKey === selectedKey}>
              ${entry.label}
            </option>
          `,
        )}
      </optgroup>
    `;
  });
}
